/**
 * FormViews for the user, profile, notification-settings, notes and filter
 * forms, plus applying their POSTed models to the in-tab user table.
 */
import { iso, now } from '../../clock';
import { Persona } from '../../personas';
import { fakeUlid } from '../../world';
import {
    FormNode,
    choice,
    choiceField,
    emailField,
    field,
    formView,
    hiddenField,
    lightswitchField,
    passwordField,
    textField,
    textareaField,
    withError,
} from './forms';
import { AdminUser, ROLE_LABELS, ROLE_ORDER, adminUsers, findNote, recordUserChange, userForPersona } from './users';

const roleChoices = () => ROLE_ORDER.map(r => choice(r, ROLE_LABELS[r]));

const METHOD_CHOICES = () => [choice('SMS'), choice('EMAIL'), choice('PUSH')];

const STREAM_CHOICES = () => [
    choice('sync', 'Data sync problems'),
    choice('support', 'Support request updates'),
    choice('digest', 'Weekly KPI digest'),
    choice('compliance', 'Compliance expiry reminders'),
    choice('board', 'Board pack ready'),
];

/** GET /api/user/form[/{id}] — `u` undefined for create. */
export function userFormView(u: AdminUser | undefined, persona: Persona): FormNode {
    const self = !!u && u.email === persona.email;
    return formView(
        'user',
        {
            email: emailField('user', 'email', { value: u?.email ?? '', required: true }),
            username: textField('user', 'username', { value: u?.username ?? '' }),
            name: textField('user', 'name', { value: u?.name ?? '', required: true }),
            role: choiceField('user', 'role', roleChoices(), {
                value: u?.roles[0] ?? 'ROLE_USER',
                required: true,
                disabled: self,
                help: self ? 'You can not change your own role' : '',
            }),
            locked: lightswitchField('user', 'locked', !!u?.locked, {
                disabled: self,
                help: self ? 'You can not lock your own account' : null,
                attr: { off_label: 'User can sign in.', on_label: 'User can no longer sign in.' },
            }),
            plainPassword: passwordField('user', 'plainPassword', {
                label: 'New password',
                help: u ? "Leave empty if you don't want to change the password" : 'Users can change their password after signing in themselves.',
            }),
            plainPasswordConfirm: passwordField('user', 'plainPasswordConfirm', { label: 'Repeat password' }),
        },
        'User',
    );
}

/** Validates and applies a user form POST. Returns the user, or a 400 FormView. */
export function saveUserForm(u: AdminUser | undefined, persona: Persona, body: Record<string, any>): { user?: AdminUser; errors?: FormNode } {
    const b = body || {};
    const view = userFormView(u, persona);
    const email = String(b['email'] ?? u?.email ?? '').trim().toLowerCase();
    const name = String(b['name'] ?? u?.name ?? '').trim();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
        return { errors: withError(view, 'email', 'This value is not a valid email address.') };
    }
    if (!name) {
        return { errors: withError(view, 'name', 'This value should not be blank.') };
    }
    if (adminUsers().some(x => x.email === email && x.id !== u?.id)) {
        return { errors: withError(view, 'email', 'There is already an account with this email.') };
    }
    if ((b['plainPassword'] || '') !== (b['plainPasswordConfirm'] || '')) {
        return { errors: withError(view, 'plainPasswordConfirm', 'Passwords do not match.') };
    }
    const self = !!u && u.email === persona.email;
    const role = ROLE_LABELS[b['role']] && !self ? String(b['role']) : (u?.roles[0] ?? 'ROLE_USER');
    const locked = self || b['locked'] === undefined ? !!u?.locked : b['locked'] === true || b['locked'] === '1' || b['locked'] === 1;
    const username = b['username'] === undefined ? (u?.username ?? null) : String(b['username'] || '') || null;

    if (!u) {
        const created = now();
        const nu: AdminUser = {
            id: fakeUlid(`new-user:${email}:${adminUsers().length}`),
            name,
            email,
            username,
            roles: [role],
            locked,
            archived: false,
            phone: null,
            jobTitle: '',
            created,
            createdById: userForPersona(persona).id,
            notificationMethods: ['EMAIL'],
            notificationStreams: [],
            personaKey: null,
            staffId: null,
            seedHistory: [{ id: fakeUlid(`log:new:${email}`), version: 1, action: 'create', username: persona.email, loggedAt: iso(created), data: { email, name, roles: [role], locked } }],
        };
        adminUsers().unshift(nu);
        return { user: nu };
    }

    const changes: Record<string, unknown> = {};
    if (u.email !== email) changes['email'] = email;
    if (u.name !== name) changes['name'] = name;
    if (u.username !== username) changes['username'] = username;
    if (u.roles[0] !== role) changes['roles'] = [role];
    if (u.locked !== locked) changes['locked'] = locked;
    if (b['plainPassword']) changes['password'] = '(changed)';
    Object.assign(u, { email, name, username, roles: [role], locked });
    recordUserChange(u, persona, changes);
    return { user: u };
}

/** GET /api/user/profile — the signed-in user's own profile. */
export function profileFormView(u: AdminUser): FormNode {
    return formView(
        'user_profile',
        {
            email: emailField('user_profile', 'email', { value: u.email, disabled: true, help: 'Contact an administrator to change your email address.' }),
            username: textField('user_profile', 'username', { value: u.username ?? '', disabled: true }),
            name: textField('user_profile', 'name', { value: u.name, required: true }),
            phone: field('user_profile', 'phone', ['text', 'tel', 'mobile_phone'], { value: u.phone ?? '', label: 'Mobile' }),
            notificationMethods: choiceField('user_profile', 'notificationMethods', METHOD_CHOICES(), {
                multiple: true,
                value: [...u.notificationMethods],
                label: 'Notification methods',
                prefixes: ['choice', 'sf_enum'],
            }),
            plainPassword: passwordField('user_profile', 'plainPassword', { label: 'New password', help: "Leave empty if you don't want to change the password" }),
            plainPasswordConfirm: passwordField('user_profile', 'plainPasswordConfirm', { label: 'Repeat password' }),
        },
        'Profile',
    );
}

export function saveProfile(u: AdminUser, persona: Persona, body: Record<string, any>): { user?: AdminUser; errors?: FormNode } {
    const b = body || {};
    if ((b['plainPassword'] || '') !== (b['plainPasswordConfirm'] || '')) {
        return { errors: withError(profileFormView(u), 'plainPasswordConfirm', 'Passwords do not match.') };
    }
    const name = String(b['name'] ?? u.name).trim() || u.name;
    const phone = b['phone'] === undefined ? u.phone : String(b['phone'] || '').trim() || null;
    const methods = Array.isArray(b['notificationMethods']) ? b['notificationMethods'].filter((m: unknown) => ['SMS', 'EMAIL', 'PUSH'].includes(String(m))) : u.notificationMethods;
    const changes: Record<string, unknown> = {};
    if (name !== u.name) changes['name'] = name;
    if (phone !== u.phone) changes['phone'] = phone;
    if (b['plainPassword']) changes['password'] = '(changed)';
    Object.assign(u, { name, phone, notificationMethods: methods });
    recordUserChange(u, persona, changes);
    return { user: u };
}

/** GET /api/ext/form/notification-settings/{id} */
export function notificationSettingsFormView(u: AdminUser): FormNode {
    return formView(
        'notification_settings',
        {
            notificationMethods: choiceField('notification_settings', 'notificationMethods', METHOD_CHOICES(), {
                multiple: true,
                value: [...u.notificationMethods],
                label: 'Send notifications by',
                help: 'Push notifications need this device to allow notifications.',
            }),
            notificationStreams: choiceField('notification_settings', 'notificationStreams', STREAM_CHOICES(), {
                multiple: true,
                value: [...u.notificationStreams],
                label: 'Notify me about',
            }),
        },
        'Notification Settings',
    );
}

export function saveNotificationSettings(u: AdminUser, body: Record<string, any>): AdminUser {
    const b = body || {};
    if (Array.isArray(b['notificationMethods'])) {
        u.notificationMethods = b['notificationMethods'].map(String);
    }
    if (Array.isArray(b['notificationStreams'])) {
        u.notificationStreams = b['notificationStreams'].map(String);
    }
    return u;
}

/** GET /api/notes/form[/{noteId}] */
export function noteFormView(noteId?: string, objectId?: string): FormNode {
    const existing = noteId ? findNote(noteId) : null;
    return formView(
        'note',
        {
            message: textareaField('note', 'message', { value: existing?.note.message ?? '', label: 'Note', required: true, attr: { rows: 3, placeholder: 'Add a note…' } }),
            objectId: hiddenField('note', 'objectId', { value: existing?.objectId ?? objectId ?? '', label: false }),
        },
        'Note',
    );
}

/** GET /api/ext/filter/form/{class} — users get role + locked; anything else an empty filter. */
export function filterFormView(className: string): FormNode {
    if (/\\?User$/.test(className)) {
        return formView(
            'user_filter',
            {
                role: choiceField('user_filter', 'role', roleChoices(), { placeholder: 'Any role', label: 'Role' }),
                locked: choiceField('user_filter', 'locked', [choice('1', 'Yes'), choice('0', 'No')], { placeholder: 'Any', label: 'Locked' }),
            },
            'Filter',
        );
    }
    const short = className.split('\\').pop() || 'item';
    return formView(`${short.replace(/[^A-Za-z]/g, '').toLowerCase()}_filter`, {}, 'Filter');
}

/** Any other /api/ext/form/{slug} (boilerplate example forms): an empty, valid form. */
export function emptyFormView(slug: string): FormNode {
    return formView(slug.replace(/[^A-Za-z0-9_]/g, '_'), {});
}
